import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { Autoresponders } from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';

import { checkForErrors } from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { DefaultMessagesData } from './load-default-messages';

/**
 * Saves one channel's autoresponders.
 *
 * Goes through the BULK endpoint (`pypesPutCustomersPypes*`) with a single pype
 * id, NOT the per-pype PUT (`pypesPutCutomerByIdPypesPypeById*`) — that action
 * cannot express a partial update. Its request transform serializes EVERY
 * `PypeUpdateInput` field into the REST body, so a mutation carrying only
 * `autoresponders` arrives at the pypes API as
 * `{"autoresponders": {…}, "name": null, "bot_retry": {"attempts": null,
 * "timeout": null}, "agent_routing": null, "masking_rules": null, …}`. A PUT
 * there is a full replace, and the API rejects that body outright (`name:
 * ["can't be blank"]`, `bot_retry: ["attempts must be a non-negative integer"]`)
 * — which Hasura in turn fails to parse as an action error, surfacing as the
 * opaque `ActionWebhookErrorResponse … key "message" not found` toast.
 *
 * Backfilling those nulls from context is NOT a fix: `masking_rules` isn't
 * readable through the `Pype` GraphQL type, so a full replace the API did accept
 * would silently wipe each saved channel's Data Privacy assignment.
 *
 * The bulk endpoint instead takes `{pypeIds, updates}` and applies `updates` as
 * a changeset — the same reason Error Handling can send `botRetry` on its own
 * (see update-error-handling.ts).
 */
export const updateDefaultMessages = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<(DefaultMessagesData & { cb?: () => void }) | void> => {
  if (event.type !== 'updateDefaultMessages') {
    return;
  }

  const projectId = ctx.project?.id;
  const customerId = ctx.project?.projectProStudioSetting?.customerId;

  if (!projectId || !customerId) {
    throwErrorWithId('Project ID and Customer ID are required');
    return;
  }

  const { id, env, autoresponders, cb } = event.payload;

  const bulkMutation = {
    live: urqlGql.bulkUpdatePypesLive,
    sandbox: urqlGql.bulkUpdatePypesSandbox,
  }[env];

  const response = await bulkMutation({
    projectId,
    customerId,
    bulkPypeUpdateInput: { pypeIds: [id], updates: { autoresponders } },
  });
  checkForErrors(response);

  // The bulk mutation selects `{id, name}` only, so reflect what was just sent
  // rather than a response body. Note the key rename: the input field is
  // `technicalError`, the read model's is `technical_error` — which is what the
  // page reads back through `toPlatformMessages`.
  const saved: Autoresponders = {
    busy: autoresponders.busy,
    offline: autoresponders.offline,
    technical_error: autoresponders.technicalError,
  };

  const current = ctx.defaultMessagesPypes ?? { live: [], sandbox: [] };

  return {
    defaultMessagesPypes: {
      ...current,
      [env]: current[env].map((pype) =>
        pype.id === id ? { ...pype, autoresponders: saved } : pype
      ),
    },
    cb,
  };
};
